import { ClosetUnit, Section, Wall } from "./domain";
import logging from './logging';
import { select } from "./utils";

const log = logging('measurement');

export let availableSlots: number[];

export const setAvailableSlots = (list: number[]) => {
    availableSlots = Array.from(list);
}

export const getAvailableSlots = () => availableSlots;

/**
 * Consults the chain of sections back to the root of the unit to discover the
 * origin of this unit with reference to the floor, i.e. y = 0. This is the
 * vertical point where the void space of the section begins, i.e. the top
 * surface of the fixed shelf below it.
 */
export const elevation = (section: Section): number => {
    if(section.parent instanceof Section) {
        if(section.parent.orientation == 'column') {
            // If the parent is in column orientation, then the sections are
            // stacked vertically
            return section.position.y + elevation(section.parent);
        } else {
            // otherwise the child sections are stacked horizontally and have
            // exactly the same elevation as their parent
            return elevation(section.parent);
        }
    } else if(section.parent instanceof ClosetUnit) {
        return section.parent.bottomShelf + section.parent.thickness - 3/8;
    } else if(section.parent instanceof Wall) {
        log.error('measurement elevation() unimplemented parent type: Wall. Elevation will be inaccurate.');
    } else {
        return 0;
    }
}

/*
 * The holes for "fixed"/cammed shelves are drilled 3/8" from the bottom of the
 * shelf, regardless of material thickness. Directly in the middle for the most
 * common 3/4" material.
 */

/**
 * A tool for making approximate comparisons where rounding errors or other
 * factors might make simple numerical equality difficult. This is appropriate
 * for use on numbers that represent inches because the precision is one
 * thousandth of an inch, which may be too coarse for other units.
 */
export const approx = (a: number, b: number) => {
    return Math.abs(a - b) < 0.02;
}

/**
 * Test if a is greater than b by a margin greater than 1/1000th of an inch.
 */
export const approxGreaterThan = (a: number, b: number) => {
    return a - b > 0.02;
}

/**
 * Test if a is greater than or equal to b, considering them equal if they are
 * within 1/1000th of an inch.
 */
export const approxGteq = (a: number, b: number) => {
    return approxGreaterThan(a, b) || approx(a, b);
}

/**
 * Test if a is less than b by a margin greater than 1/1000th of an inch.
 */
export const approxLessThan = (a: number, b: number) => {
    return b - a > 0.02;
}

/**
 * Test if a is less than or equal to b, considering them equal if they are
 * within 1/1000th of an inch.
 */
export const approxLteq = (a: number, b: number) => {
    return approxLessThan(a, b) || approx(a, b);
}

/**
 * This is (sort of) a decimal truncation procedure, also sort of a floor
 * procedure. If you want to truncate a float to three decimal places, call
 * `trunc(f, 3)`. Thus pi truncated to three decimal places would yield 3.141.
 * Notice that properly rounded, that would be 3.142. Because of some
 * binary representation issues, rounding with this method doesn't always round
 * decimal fives in the same direction, so I'm calling this truncation instead.
 * This only really works for positive n.
 */
export const trunc = (x: number, n: number) => {
    const f = Math.pow(10, n);
    return Math.floor(x * f) / f;
}

/**
 * Check if the interval described by (a1, a2) overlaps the interval described
 * by (b1, b2). These intervals are open, so if they only share a point in
 * common, then they are not considered to overlap. Note that due to floating
 * point error, you may need to round or truncate the input to this function to
 * achieve a reliable result.
 *
 * Note that this will return false if a1 == a2 or b1 == b2. Checking whether
 * the point a is between b1 and b2 is much easier, and you probably want to
 * do that directly with `a > b1 && a < b2` instead.
 */
export const overlaps =
    (a1: number, a2: number, b1: number, b2: number): boolean =>
{
    // Since we don't know if a1 < a2, we make d1 and d2 such that we're sure
    // d1 < d2, and that geometrically, d covers the same interval as a. The
    // same is true for b and e. Finally, we find points f1 and f2 which are
    // the start and end of the overlapping sections. If the a and b intervals
    // don't overlap, then the f interval ends up being "backwards", and we
    // return false.
    const
        d1 = Math.min(a1, a2),
        d2 = Math.max(a1, a2),

        e1 = Math.min(b1, b2),
        e2 = Math.max(b1, b2),

        f1 = Math.max(d1, e1),
        f2 = Math.min(d2, e2);

    return f1 < f2;
}

/**
 * Truncates the parameters to 1 decimal place and delegates the comparison to
 * the overlaps function. This avoids floating point error.
 */
export const approxOverlaps =
    (a1: number, a2: number, b1: number, b2: number): boolean =>
{
    return overlaps(trunc(a1, 1), trunc(a2, 1), trunc(b1, 1), trunc(b2, 1));
}

/**
 * Converts a length in millimeters to an equivalent length in inches.
 * @function mm2in
 */
export const mm2in = (mm: number) => {
    return mm / 25.4;
}

/**
 * Converts a length in inches to an equivalent length in millimeters.
 * @function in2mm
 */
export const in2mm = (inches: number) => {
    return inches * 25.4;
}

/**
 * For the moment I'm calling a hole in a boring panel a 'slot'. The slots are
 * spaced at 32 millimeters, approximately 1.26 inches apart. The slot number
 * is an integer number of 32mm intervals.
 * @function slot2in
 */
export const slot2in = (slot: number) => {
    return mm2in(slot * 32);
}

/**
 * Given a measurement in iches, finds the nearest 'slot' in a panel with holes
 * at 32 mm intervals.
 * @function in2slot
 */
export const in2slot = (inches: number) => {
    return Math.round(in2mm(inches)/32);
}

/**
 * Rounds a measurement in inches to the nearest slot and returns the
 * measurement in inches. This is useful for snapping to slots. Note that the
 * input and output refer to absolute elevations and account for a 3/8"
 * elevation of the first slot.
 */
export const round2slot = (inches: number) => {
    //return 0.375 + slot2in(in2slot(inches - 0.375));
    return select(inches, availableSlots);
}

export const closestIndex = (inches: number) => {
    let m = Infinity;
    for(let i=0; i < availableSlots.length; i++) {
        const slot = availableSlots[i];
        const d = Math.abs(slot - inches);
        if(d > m) {
            return i - 1;
        }
        m = d;
    }
}

/**
 * Round the inch measurement up to the nearest slot, but if the measurement is
 * already (approximately) on a slot, then return the exact slot value. Note
 * that the input and output refer to absolute elevations.
 * @returns a measurement in inches rounded to a 32mm increment.
 */
export const slotCeil = (inches: number) => {
    for(let i=0; i < availableSlots.length; i++) {
        if(Math.abs(availableSlots[i] - inches) < 0.02) {
            return availableSlots[i];
        }
        if(availableSlots[i] > inches) {
            return availableSlots[i];
        }
    }
}

/**
 * This is like slotCeil except that it always returns the next slot higher
 * rather than returning the current slot when it's close.
 */
export const slotAbove = (inches: number) => {
    const len = availableSlots.length;
    for(let i=0; i < len; i++) {
        if(availableSlots[i] - inches > 0.02) {
            return availableSlots[i];
        }
    }
    return availableSlots[len-1];
}

/**
 * This is like slotFloor except that it always returns the next slot lower
 * rather than returning the current slot when it's close.
 */
export const slotBelow = (inches: number) => {
    const len = availableSlots.length;
    for(let i=2; i < len; i++) {
        if(availableSlots[i] - inches > 0.02) {
            return availableSlots[i-2];
        }
    }
    return availableSlots[len-2];
}


/**
 * Round the inch measurement down to the nearest slot, but if the measurement
 * is already (approximately) on a slot, then return the exact slot value. Note
 * that the input and output refer to absolute elevations.
 * @returns a measurement in inches rounded to a 32mm increment.
 */
export const slotFloor = (inches: number) => {
    for(let i=1; i < availableSlots.length; i++) {
        if(Math.abs(availableSlots[i] - inches) < 0.02) {
            return availableSlots[i];
        }
        if(availableSlots[i] > inches) {
            return availableSlots[i-1];
        }
    }
}

export type MeasurementUnits = "inches" | "mm";

/**
 * Convert the internal storage format (a decimal number of inches) to either
 * inches, rounded to the nearest 16th of an inch, or millimeters, rounded to
 * the nearest tenth of a millimeter.
 */
export const toDisplayLength = (inches: number, units: MeasurementUnits): string => {
    switch(units) {
        case "inches":
            return String(Math.round(inches * 16) / 16);
        case "mm":
            return String(Math.round(inches * 25.4));
        default:
            log.error(`Unknown measurement unit "${units}"`);
            return "";
    }
};

/**
 * Convert a string previously formatted for display back to the internal
 * numerical storage format (a decimal number of inches), given the units in
 * which the item was displayed.
 */
export const fromDisplayLength = (value: string, units: MeasurementUnits): number => {
    switch(units) {
        case "inches":
            return Number(value);
        case "mm":
            return Number(value) / 25.4;
        default:
            log.error(`Unknown measurement unit "${units}"`);
            return 0;
    }
};

/**
 * Like toDisplayLength but also appends `"` or `mm` to indicate which units
 * system is in use.
 */
export const toDisplayLengthWithUnitsText = (inches: number, units: MeasurementUnits) => {
    switch(units) {
        case "inches":
            return toDisplayLength(inches, units) + '"';
        case "mm":
            return toDisplayLength(inches, units) + 'mm';
        default:
            log.error(`Unknown measurement unit "${units}"`);
            return "";
    }
}

